import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import {
  closeLoaderWithMessage,
  openLoaderWithMessage,
  showMessage,
} from "../messages/messagesActions";
import {
  FETCH_MECHANIC_LIST_FAILED,
  FETCH_MECHANIC_LIST_PROGRESS,
  FETCH_MECHANIC_LIST_SUCCESS,
  IGetMechanic,
  IMechanic,
  IMechanicFilters,
  IUpsertMechanic,
  MECHANIC_LIST_CSV_HEADERS,
  SET_MECHANIC_LIST_FILTERS,
  UPSERT_MECHANIC_SUCCESS,
} from "./mechanicList.types";

export const getMechanicListAsync =
  (
    urlParams?: string,
    onCallback?: (isSuccess: boolean, data?: IMechanic[], totalRecords?: number) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch({ type: FETCH_MECHANIC_LIST_PROGRESS });
        const finalUrl = `/partner/get-mechanic-list${urlParams ? `?${urlParams}` : ""}`;
        const res = await api.get(finalUrl);

        const data: IMechanic[] = res?.data?.data || res?.data?.result || [];
        const totalRecords = res?.data?.total_records || res?.data?.totalRecords || data.length;

        dispatch({
          type: FETCH_MECHANIC_LIST_SUCCESS,
          payload: { data, totalRecords },
        });

        if (onCallback) {
          onCallback(true, data, totalRecords);
        }
      } catch (err: any) {
        dispatch({ type: FETCH_MECHANIC_LIST_FAILED });
        if (onCallback) {
          onCallback(false);
        }
      }
    };

export const upsertMechanicAsync =
  (
    mechanicData: Partial<IMechanic>,
    onCallback?: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        const res = await api.post("/partner/upsert-mechanic", mechanicData);
        const resData = res?.data?.data || mechanicData;

        dispatch({
          type: UPSERT_MECHANIC_SUCCESS,
          payload: resData,
        });

        dispatch(
          showMessage({
            message: mechanicData.id ? "Mechanic updated successfully" : "Mechanic created successfully",
            type: "success",
            displayAs: "snackbar",
          })
        );

        if (onCallback) {
          onCallback(true);
        }
      } catch (err: any) {
        dispatch(
          showMessage({
            message: err?.response?.data?.message || "Failed to save mechanic details",
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallback) {
          onCallback(false);
        }
      }
    };

export const upsertMechanicFormAsync =
  (
    values: IUpsertMechanic,
    tokenOrCallback?: string | ((isSuccess: boolean, msg?: string) => void),
    onCallbackParam?: (isSuccess: boolean, msg?: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      const token = typeof tokenOrCallback === "string" ? tokenOrCallback : undefined;
      const onCallback = typeof tokenOrCallback === "function" ? tokenOrCallback : onCallbackParam;

      try {
        dispatch(openLoaderWithMessage("Saving mechanic details, please wait..."));
        const config = token ? { headers: { "auth-Token": token } } : {};
        const res = await api.post("upsert-mechanic", { ...values }, config);
        const msg = res.data.msg || "Mechanic saved successfully";
        dispatch(
          showMessage({
            message: msg,
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallback) {
          onCallback(true, msg);
        }
      } catch (err: any) {
        const errorMsg =
          err?.response?.data?.msg || err?.response?.data?.message || "Failed to save mechanic details";
        dispatch(
          showMessage({
            message: errorMsg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallback) {
          onCallback(false, errorMsg);
        }
      } finally {
        dispatch(closeLoaderWithMessage());
      }
    };

export const fetchMechanicDetailsApiAsync =
  (
    mechanicUUID: string,
    tokenOrCallback?: string | ((isSuccess: boolean, result?: IGetMechanic[], msg?: string) => void),
    onCallbackParam?: (isSuccess: boolean, result?: IGetMechanic[], msg?: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      const token = typeof tokenOrCallback === "string" ? tokenOrCallback : undefined;
      const onCallback = typeof tokenOrCallback === "function" ? tokenOrCallback : onCallbackParam;

      try {
        dispatch(openLoaderWithMessage("Fetching mechanic details..."));
        const config = token ? { headers: { "auth-Token": token } } : {};
        const res = await api.get(
          "/get-mechanic-list?mechanic_id_v=" + mechanicUUID,
          config
        );
        const result = res.data.result || [];
        if (onCallback) {
          onCallback(true, result);
        }
      } catch (err: any) {
        const errorMsg =
          err?.response?.data?.msg || err?.response?.data?.message || "Failed to fetch mechanic details";
        dispatch(
          showMessage({
            message: errorMsg,
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallback) {
          onCallback(false, undefined, errorMsg);
        }
      } finally {
        dispatch(closeLoaderWithMessage());
      }
    };

export const uploadMechanicDocAsync =
  (
    file: File,
    tokenOrCallback?: string | ((isSuccess: boolean, key?: string) => void),
    onCallbackParam?: (isSuccess: boolean, key?: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      const token = typeof tokenOrCallback === "string" ? tokenOrCallback : undefined;
      const onCallback = typeof tokenOrCallback === "function" ? tokenOrCallback : onCallbackParam;

      try {
        dispatch(openLoaderWithMessage("Uploading document, please wait..."));
        const formdata = new FormData();
        formdata.append("file", file);
        const config = token ? { headers: { "auth-Token": token } } : {};
        const res = await api.post("/upload-docs", formdata, config);
        const fileUrl = res.data.Key;
        dispatch(
          showMessage({
            message: "File uploaded successfully",
            type: "success",
            displayAs: "snackbar",
          })
        );
        if (onCallback) {
          onCallback(true, fileUrl);
        }
      } catch (err: any) {
        dispatch(
          showMessage({
            message:
              err?.response?.data?.msg || err?.response?.data?.message || "Failed to upload file",
            type: "error",
            displayAs: "snackbar",
          })
        );
        if (onCallback) {
          onCallback(false);
        }
      } finally {
        dispatch(closeLoaderWithMessage());
      }
    };

export const setMechanicFilters = (filters: Partial<IMechanicFilters>): AnyAction => ({
  type: SET_MECHANIC_LIST_FILTERS,
  payload: filters,
});

export const exportMechanicListCSV = (filteredList: IMechanic[]) => {
  const rows = filteredList.map((m) => [
    `"${m.id}"`,
    `"${(m.user_name || m.name || "").replace(/"/g, '""')}"`,
    `"${m.contact_no || m.mobile_number || ""}"`,
    `"${m.nodal || m.nodal_name || ""}"`,
    `"${m.mechanic_shift || m.shift || ""}"`,
    `"${m.mechanic_type || m.type || ""}"`,
    `"${m.rsa_mechanic || ""}"`,
    `"${m.sow_mechanic || ""}"`,
    `"${m.towing_mechanic || ""}"`,
    `"${m.agreed_amount || ""}"`,
    `"${m.extra_amount || ""}"`,
    `"${m.created_by || ""}"`,
    `"${m.create_dt || ""}"`,
    `"${m.city || ""}"`,
    `"${m.state || ""}"`,
    `"${m.total_services_done || ""}"`,
    `"${m.total_earned_amount || ""}"`,
  ]);

  const csvContent =
    "data:text/csv;charset=utf-8," +
    [MECHANIC_LIST_CSV_HEADERS.join(","), ...rows.map((e) => e.join(","))].join("\n");
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", `mechanic_list_${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const filterMechanicList = (
  list: IMechanic[],
  filters: Partial<IMechanicFilters>
): IMechanic[] => {
  const {
    shift = "All Shifts",
    type = "All Types",
    rsaSow = "Search RSA/SOW",
    searchBy = "Search By",
    searchQuery = "",
  } = filters;

  return list.filter((item) => {
    const shiftVal = item.mechanic_shift || item.shift;
    if (
      shift !== "All Shifts" &&
      shiftVal &&
      !shiftVal.toLowerCase().includes(shift.toLowerCase().replace(" shift", ""))
    ) {
      return false;
    }
    const typeVal = item.mechanic_type || item.type;
    if (
      type !== "All Types" &&
      typeVal &&
      !typeVal.toLowerCase().includes(type.toLowerCase())
    ) {
      return false;
    }
    // if (rsaSow !== "Search RSA/SOW") {
    //   if (rsaSow === "RSA" && item.rsa_mechanic !== "YES" && item.rsa_sow !== "RSA") return false;
    //   if (rsaSow === "SOW" && item.sow_mechanic !== "YES" && item.rsa_sow !== "SOW") return false;
    // }
    if (searchQuery && searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      const nameVal = (item.user_name || item.name || "").toLowerCase();
      const phoneVal = (item.contact_no || item.mobile_number || "").toLowerCase();
      const nodalVal = (item.nodal || item.nodal_name || "").toLowerCase();

      if (searchBy === "Name") {
        return nameVal.includes(query);
      } else if (searchBy === "Mobile Number") {
        return phoneVal.includes(query);
      } else if (searchBy === "Nodal Name") {
        return nodalVal.includes(query);
      } else {
        return (
          nameVal.includes(query) ||
          phoneVal.includes(query) ||
          nodalVal.includes(query)
        );
      }
    }
    return true;
  });
};

export const fetchMechanicsListByPlaceIdAsync =
  (
    placeId: string,
    radius: number,
    onCallback?: (isSuccess: boolean, data?: IMechanic[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch({ type: FETCH_MECHANIC_LIST_PROGRESS });
        const res = await api.get(`/partner/get-mechanic-list?place_id=${placeId}&radius=${radius}`);
        const data: IMechanic[] = res?.data?.data || res?.data?.result || [];
        const totalRecords = res?.data?.total_records || res?.data?.totalRecords || data.length;

        dispatch({
          type: FETCH_MECHANIC_LIST_SUCCESS,
          payload: { data, totalRecords },
        });

        if (onCallback) {
          onCallback(true, data);
        }
      } catch (err: any) {
        dispatch({ type: FETCH_MECHANIC_LIST_FAILED });
        if (onCallback) {
          onCallback(false);
        }
      }
    };
